import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { CartActions } from '@spartacus/cart/base/core';
import { AuthStatePersistenceService, createFrom, EventService, RouterState, RoutingService } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Ga4GgCartUpdateEntrySuccessEvent } from './ga4-gg-cart-update-entry-success-event';
import { ActiveCartFacade } from '@spartacus/cart/base/root';

@Injectable({
  providedIn: 'root',
})
export class Ga4GgCartUpdateEntrySuccessEventBuilder {
  constructor(
    private eventService: EventService,
    private authStatePersistenceService: AuthStatePersistenceService,
    protected activeCartService: ActiveCartFacade,
    protected actionsSubject: ActionsSubject,
    protected event: EventService,
    protected routing: RoutingService
  ) {
    const ga4GgCartUpdateEntrySuccessCleanUpEventSource = this.getAction(CartActions.CART_UPDATE_ENTRY_SUCCESS).pipe(
      map(() =>
        createFrom(Ga4GgCartUpdateEntrySuccessEvent, {
          ecommerce: null,
        })
      )
    );

    eventService.register(Ga4GgCartUpdateEntrySuccessEvent, ga4GgCartUpdateEntrySuccessCleanUpEventSource);

    const ga4GgCartUpdateEntrySuccessEventSource = this.getAction(CartActions.CART_UPDATE_ENTRY_SUCCESS).pipe(
      switchMap((action) => {
        return of(action).pipe(withLatestFrom(this.activeCartService.getEntries(), this.routing.getRouterState()));
      }),
      map(([action, entries, routerState]) =>
        createFrom(Ga4GgCartUpdateEntrySuccessEvent, {
          event:
            entries[Number(action.payload.entryNumber)].quantity! > action.payload.quantity
              ? 'remove_from_cart'
              : 'add_to_cart',
          visitor_type: authStatePersistenceService.isUserLoggedIn() ? 'logged_in' : 'logged-out',
          ecommerce: {
            currency: entries[Number(action.payload.entryNumber)].basePrice?.currencyIso,
            value:
              entries[Number(action.payload.entryNumber)].quantity! > action.payload.quantity
                ? (entries[Number(action.payload.entryNumber)].quantity! - action.payload.quantity) *
                  entries[Number(action.payload.entryNumber)].basePrice?.value!
                : (action.payload.quantity - entries[Number(action.payload.entryNumber)].quantity!) *
                  entries[Number(action.payload.entryNumber)].basePrice?.value!,
            items: this.getItemFromSuccessfullyEditedEntry(
              entries[Number(action.payload.entryNumber)],
              action.payload.quantity,
              routerState
            ),
          },
        })
      )
    );

    eventService.register(Ga4GgCartUpdateEntrySuccessEvent, ga4GgCartUpdateEntrySuccessEventSource);
  }

  private getItemFromSuccessfullyEditedEntry(successfullyEditedEntry, quantity, routerState: RouterState) {
    const items: {
      item_id?: string;
      item_name?: string;
      price?: number;
      quantity?: number;
      item_list_name?: string;
    }[] = [];

    const item = {
      item_id: successfullyEditedEntry.product.code,
      item_name: successfullyEditedEntry.product.modelMark,
      price: successfullyEditedEntry.basePrice.value,
      quantity:
        successfullyEditedEntry.quantity > quantity
          ? successfullyEditedEntry.quantity - quantity
          : quantity - successfullyEditedEntry.quantity,
      item_list_name: routerState.state.semanticRoute === 'cart' ? 'Cart' : 'Minicart',
    };

    successfullyEditedEntry.product.categoryHierarchy?.forEach((category, index) => {
      item['item_category' + (index.toString() !== '0' ? (index + 1).toString() : '')] = category.name;
    });

    Object.keys(item).forEach((key) => (item[key] === undefined ? delete item[key] : {}));

    items.push(item);

    return items;
  }

  protected getAction(actionType: string | string[]): Observable<{ type: string; payload?: any }> {
    return this.actionsSubject.pipe(ofType(...([] as string[]).concat(actionType)));
  }
}
